import { Box, Stack, Typography, Tooltip, IconButton, List, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ReplayIcon from "@mui/icons-material/Replay";
import { FC, useEffect } from "react";
import { KeywordRule } from "@/gen/api/securefile";
import { useZSliceUnderContext } from "@/utils/zustand-form/hooks";
import { FormState, FormToken } from "@/utils/zustand-form/types";

import KeywordRuleLine from './KeywordRuleLine';
import { EditorLineSx } from './InfoTypeEditor.types';
import { CollectEditsMetadata, CollectEditsProtocol } from "@/utils/zustand-form/ext/collectEdits";


const KeywordRuleEditor : FC<{
  parentToken: FormToken;
  baseData: KeywordRule[];
}> = ({
  parentToken,
  baseData: prevData,
}) => {
  // Zustand mode - always call hook (don't specify generic - let TypeScript infer from stageInit)
  const zustandUtils = useZSliceUnderContext({
    parentToken: parentToken ?? null,
    pathFragment: 'keywordRules',
    prevData,
    stageInit: () => ({
      added: [] as KeywordRule[],
      removed: new Set<number>(),
    }),
    extraStates: {},
  });
  const { states, setStates, token, installProtocolMetadata } = zustandUtils

  useEffect(() => {
    return installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: () => Promise.resolve(),
      overrideCollect: async (node, safeInvokeNode, safeChildrenCollection) => {
        const states = node.state as FormState<{
          added: KeywordRule[];
          removed: Set<number>;
        }, KeywordRule[]>
        const ids = [
          ...[...new Array(states.prevData.length)].map((_x, i) => i).filter(i => !states.stage.removed.has(i)).map(i => `r[${i}]`),
          ...states.stage.added.map((_x, i) => i).map(i => `a[${i}]`).filter(x => x != null),
        ];
        const collected : KeywordRule[] = []
        for (const id of ids) {
          const childNode = safeChildrenCollection.get(id)
          if (childNode) {
            const result = await safeInvokeNode(childNode)
            collected.push(result)
          }
        }
        return collected
      },
    })
  }, [installProtocolMetadata])

  useEffect(() => {
    setStates(v => ({...v, prevData, stage: {added: [], removed: new Set()}, touched: {}}));
  }, [prevData, setStates]);

  const actionBtn = (d: KeywordRule, i: number) => {
    const atAdded = states.stage.added.indexOf(d);
    const isRestore = states.stage.removed.has(i);
    const actionFn = () => setStates((v: any) => {
      if (atAdded > -1) {
        delete v.stage.added[atAdded];
      } else if (isRestore) {
        v.stage.removed.delete(i);
      } else {
        v.stage.removed.add(i);
      }
      const touched = v.stage.removed.size > 0 || v.stage.added.some((x: any) => !!x);
      return {...v, touched: touched ? { edit: true } : {}};
    });

    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>);
  };

  const classOf = (d: KeywordRule, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined;
  };

  const renderLine = (d: KeywordRule, i: number, key: string, className?: string) => (
    <KeywordRuleLine
      parentToken={token}
      className={className}
      key={i}
      r_key={key}
      prevData={d}
      actionButton={actionBtn(d, i)}
    />
  );

  return (
    <Box sx={EditorLineSx}>
      <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
        <Typography variant="subtitle2">Context Rules</Typography>
        <Tooltip title="Add Context Rule">
          <IconButton color="primary"
            onClick={() => setStates((v: any) => {
              v.stage.added.push({ words: [], regex: null, windowBefore: 100, windowAfter: 100, scoreAdjustment: 0.45});
              return {...v, touched: { edit: true }};
            })}
          >
            <AddIcon /></IconButton>
        </Tooltip>
      </Stack>
      <Alert severity="info" sx={{ mb: 1 }}>
        A context rule is used to adjust the matching score derived from the Pattern Detection above. The context keywords or context pattern is searched within the Context Window specified below. If any match is found, the matching score derived from Pattern Detection is incremented by the Context Score. If matched by multiple context rules, the matching score is incremented by the highest context score.
      </Alert>
      <List disablePadding>
        {states.stage.added.map((d: KeywordRule, i: number) => (
          renderLine(d, i, `a[${i}]`, 'added')
        )).filter(d => !!d).reverse()}
      </List>
      <List disablePadding>
        {states.prevData.map((d: KeywordRule, i: number) => (
          renderLine(d, i, `r[${i}]`, classOf(d, i))
        ))}
      </List>
    </Box>
  );
};

export default KeywordRuleEditor;
